// ─────────────────────────────────────────────────────────────────────────────
// challanApi.js — Generated challan storage backed by the API.
// ─────────────────────────────────────────────────────────────────────────────

import { API_BASE_URL, getJsonAuthHeaders } from "../../../utils/api";

const _get = (key, fb) => { try { return JSON.parse(localStorage.getItem(key)) ?? fb; } catch { return fb; } };

const STORE = "generated_challans_v1";

const apiRequest = async (path, options = {}) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: getJsonAuthHeaders(),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.detail || "Challan request failed");
  }
  return response.json();
};

/** GET all generated challans */
export const fetchGeneratedChallans = async () => {
  try {
    const backendRecords = await apiRequest("/api/challans");
    const legacyRecords = _get(STORE, []);
    const backendKeys = new Set(
      backendRecords.map((record) => `${record.studentId}|${record.feeAssignmentKey}`),
    );
    const missingLegacy = legacyRecords.filter(
      (record) =>
        record.studentId &&
        record.feeAssignmentKey &&
        !backendKeys.has(`${record.studentId}|${record.feeAssignmentKey}`),
    );

    if (missingLegacy.length) {
      const migrated = await Promise.allSettled(
        missingLegacy.map((record) =>
          apiRequest("/api/challans", {
            method: "POST",
            body: JSON.stringify({
              studentId: record.studentId,
              feeAssignmentKey: record.feeAssignmentKey,
              categoryId: record.categoryId,
              categoryLabel: record.categoryLabel,
              categoryPrefix: record.categoryPrefix,
              selectedCategories: record.selectedCategories || [],
              components: record.components || [],
              session: record.session || "",
              academicYear: record.academicYear || "",
              month: record.month,
              dueDate: record.dueDate,
              subtotal: record.subtotal || 0,
              concessionAmount: record.concessionAmount || 0,
              netPayable: record.netPayable || 0,
              status: record.status || "Unpaid",
              classFilter: record.classFilter || null,
              sectionFilter: record.sectionFilter || null,
            }),
          }),
        ),
      );
      const successful = migrated
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      return [...backendRecords, ...successful];
    }

    return backendRecords;
  } catch (error) {
    // Preserve access to challans created by older builds if the API is
    // temporarily unavailable; every newly generated challan uses the backend.
    console.error("Could not load backend challans", error);
    return _get(STORE, []);
  }
};

/** Find generated challan for a fee assignment */
export const findChallanForFee = async (studentId, feeAssignmentKey) => {
  const all = await fetchGeneratedChallans();
  return all.find(
    (c) => String(c.studentId) === String(studentId) && c.feeAssignmentKey === feeAssignmentKey
  ) || null;
};

/**
 * Bulk-generate challans for a list of { student, feeStructure } items.
 * Returns { generated, skipped, errors, summary }
 */
export const bulkGenerateChallans = async (items, meta = {}) => {
  const all = await fetchGeneratedChallans();
  const generated = [];
  const skipped = [];
  const errors = [];

  for (const { student, feeStructure } of items) {
    try {
      if (!student?.id) {
        errors.push({ student: student?.name || "Unknown", reason: "Missing student id" });
        continue;
      }
      if (!feeStructure?.month) {
        errors.push({ student: student.name, reason: "No fee month assigned" });
        continue;
      }
      if (feeStructure.status === "Paid" || feeStructure.status === "Waived") {
        skipped.push({ student: student.name, reason: "Already paid" });
        continue;
      }
      const comps = feeStructure.components || [];
      if (!comps.length) {
        skipped.push({ student: student.name, reason: "No fee components" });
        continue;
      }

      const feeAssignmentKey = `${feeStructure.categoryId}_${feeStructure.month}`;
      const existing = all.find(
        (c) => String(c.studentId) === String(student.id) && c.feeAssignmentKey === feeAssignmentKey
      );
      if (existing) {
        skipped.push({ student: student.name, reason: "Challan already generated", challanNumber: existing.challanNumber });
        continue;
      }

      const subtotal = comps.reduce((s, c) => s + (c.amount || 0), 0);
      const concAmt = feeStructure.concessionAmount || 0;
      const netPayable = Math.max(0, subtotal - concAmt);

      const record = await apiRequest("/api/challans", {
        method: "POST",
        body: JSON.stringify({
          studentId: student.id,
          feeAssignmentKey,
          categoryId: feeStructure.categoryId,
          categoryLabel: feeStructure.categoryLabel,
          categoryPrefix: feeStructure.categoryPrefix || "CHN",
          selectedCategories: feeStructure.selectedCategories || [feeStructure.categoryId].filter(Boolean),
          components: comps.map((c) => ({ ...c })),
          session: feeStructure.session || student.academic_session || "",
          academicYear: feeStructure.academicYear || "",
          month: feeStructure.month,
          dueDate: feeStructure.dueDate || "10",
          subtotal,
          concessionAmount: concAmt,
          netPayable,
          status: "Unpaid",
          classFilter: meta.classFilter || null,
          sectionFilter: meta.sectionFilter || null,
        }),
      });

      all.push(record);
      generated.push(record);
    } catch (e) {
      errors.push({ student: student?.name || "Unknown", reason: e.message || "Generation failed" });
    }
  }

  try {
    window.dispatchEvent(new CustomEvent("challans-generated", { detail: { count: generated.length } }));
  } catch {}

  return {
    generated,
    skipped,
    errors,
    summary: {
      total: items.length,
      generated: generated.length,
      skipped: skipped.length,
      errors: errors.length,
    },
  };
};

/** Update a generated challan (due date, category, components) */
export const updateGeneratedChallan = (challanId, updates) =>
  apiRequest(`/api/challans/${challanId}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });

/** Accountant/admin/superadmin: verify parent paid-proof and mark Paid */
export const verifyPaidChallan = (challanId, note = null) =>
  apiRequest(`/api/challans/${challanId}/verify-paid`, {
    method: "POST",
    body: JSON.stringify(note ? { note } : {}),
  });

/** Update challan payment status when fee is marked paid */
export const updateChallanStatus = async (studentId, feeAssignmentKey, status) => {
  const all = await fetchGeneratedChallans();
  const challan = all.find(
    (c) => String(c.studentId) === String(studentId) && c.feeAssignmentKey === feeAssignmentKey
  );
  if (!challan) return null;
  return updateGeneratedChallan(challan.id, { status });
};

/** Delete generated challan for a fee assignment */
export const deleteGeneratedChallan = async (studentId, feeAssignmentKey) => {
  const result = await apiRequest(
    `/api/challans/student/${studentId}?fee_assignment_key=${encodeURIComponent(feeAssignmentKey)}`,
    { method: "DELETE" },
  );
  try {
    window.dispatchEvent(new CustomEvent("challans-generated", { detail: { count: 0 } }));
  } catch {}
  return result;
};

/** Map challans by feeAssignmentKey for quick lookup */
export const challansByFeeKey = async () => {
  const all = await fetchGeneratedChallans();
  const map = new Map();
  all.forEach((c) => map.set(`${c.studentId}|${c.feeAssignmentKey}`, c));
  return map;
};
